import { faSearch } from '@fortawesome/free-solid-svg-icons';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import Cookies from 'react-cookies';
import { useSelector } from 'react-redux';
import {
  COOKIES,
  VIP_QUALIFIERS_PAGE_TYPE,
} from '../../brands/default/constants';
import { HEADER_SEARCH } from '../../brands/default/content/components/header';
import { SHOW_SEARCH_LOCATION } from '../../brands/default/features';
import { LINK_SEARCH_PAGE } from '../../brands/default/links';
import { trackEvent } from '../../helpers/analytics';
import {
  getDefaultSuggestions,
  getSearchSuggestions,
} from '../../helpers/searchSuggestions';
import { parseWowcherPath, redirectToRoute } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import Icon from '../Icon';
import HeaderLocationDropDown from './HeaderLocationDropDown';
import HeaderSearchDropDown from './HeaderSearchDropDown';

// eslint-disable-next-line sonarjs/cognitive-complexity
const HeaderSearch = ({ defaultPlaceholder }) => {
  const theme = useContext(ThemeContext);
  const [searchQueryDefault, setSearchQueryDefault] = useState();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropDownVisible, setIsDropDownVisible] = useState(false);
  const [suggestions, setSuggestions] = useState(getDefaultSuggestions());
  const [suggestionFocusIndex, setSuggestionFocusIndex] = useState(-1);
  const [activeDescendent, setActiveDescendent] = useState('');
  const [isTopSearches, setIsTopSearches] = useState(true);
  const router = useRouter();
  const userLocation = useSelector((state) => state.locations.location.name);
  const isDisabled = !searchQuery || !searchQuery?.length ? 'disabled' : null;
  const dropDownTitle = isTopSearches
    ? HEADER_SEARCH.topSearchesTitle
    : HEADER_SEARCH.suggestionsTitle;
  const textInput = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [location] = useSelector((state) => [state.locations.location]);
  const lightboxLocation = Cookies.load(COOKIES.lightboxLocation);
  const locationObject = location || lightboxLocation;
  const locationName = locationObject?.name;

  const path = router.asPath || '';
  const pathUrl = parseWowcherPath(path);
  const isVipHubPage = VIP_QUALIFIERS_PAGE_TYPE.includes(pathUrl.pageType);
  const vipSearchFilter = Boolean(
    pathUrl.searchParams.get(HEADER_SEARCH.vip.toLowerCase()),
  );

  const submitSearch = (query) => {
    const baseUrl = `${LINK_SEARCH_PAGE}?q=${encodeURIComponent(query)}`;
    const searchUrl =
      isVipHubPage || vipSearchFilter ? baseUrl.concat(`&vip=true`) : baseUrl;
    redirectToRoute(searchUrl, router);
  };

  const getSuggestions = async (query) => {
    const results = await getSearchSuggestions(query, userLocation);
    if (results) {
      setSuggestions(results);
      setIsTopSearches(false);
    } else {
      setSuggestions(getDefaultSuggestions());
      setIsTopSearches(true);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setSearchQueryDebounced = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      if (query && query.length > 2) {
        getSuggestions(query);
      } else {
        setSuggestions(getDefaultSuggestions());
        setIsTopSearches(true);
      }
    }, 600),
    [],
  );

  const inputLocationHandler = () => {
    setShowModal(true);
  };

  const changeHandler = (event) => {
    setSearchQueryDebounced(event.target.value, userLocation);
  };

  const blurHandler = () => {
    setTimeout(() => setIsDropDownVisible(false), 200);
    setSuggestionFocusIndex(-1);
  };

  const focusHandler = () => {
    setIsDropDownVisible(true);
  };

  const inputKeyDownHandler = (keyEvent) => {
    // do search for enter key
    if (keyEvent.key === 'Enter' && keyEvent.target.value.length > 0) {
      submitSearch(keyEvent.target.value);
      setIsDropDownVisible(false);
      // prevent default form submit
      keyEvent.preventDefault();
    }

    // handle key events on suggestions when drop down open
    if (suggestions && suggestions.length > 0 && isDropDownVisible) {
      let temporaryIndex = suggestionFocusIndex;
      // escape
      if (keyEvent.which === 27) {
        setIsDropDownVisible(false);
        setSuggestions(getDefaultSuggestions());
      }
      // up
      if (keyEvent.which === 38) {
        if (temporaryIndex === -1) {
          // cycle to bottom
          temporaryIndex = suggestions.length - 1;
        } else {
          // move up one
          temporaryIndex = suggestionFocusIndex - 1;
        }
      }
      // down
      if (keyEvent.which === 40) {
        if (temporaryIndex >= suggestions.length - 1) {
          // cycle to unselected, top of list
          temporaryIndex = -1;
        } else {
          // move down one
          temporaryIndex = suggestionFocusIndex + 1;
        }
      }
      setSuggestionFocusIndex(temporaryIndex);
      setActiveDescendent(`suggest-li-${temporaryIndex}`);

      // if a suggestion is selected
      if (
        (keyEvent.which === 38 || keyEvent.which === 40) &&
        temporaryIndex !== -1
      ) {
        setSearchQuery(suggestions[temporaryIndex]);
        // input can't be controlled to make debounce call suggestions work
        textInput.current.value = suggestions[temporaryIndex];
      }
    }
  };

  const liClickHandler = (query) => {
    trackEvent('search_suggestion_click');
    setSearchQuery(query);
    textInput.current.value = query;
    setSuggestions(getDefaultSuggestions());
    submitSearch(query);
  };

  const searchButtonClickHandler = (event, query) => {
    event.preventDefault();
    submitSearch(query);
  };

  // on first render check if query is in url
  useEffect(() => {
    // eslint-disable-next-line id-length
    const { q } = router.query;
    if (q) setSearchQueryDefault(q);
  }, [router]);

  return (
    <>
      <form className="search-container">
        <input
          aria-activedescendant={activeDescendent}
          aria-controls="search-suggestions"
          aria-expanded={isDropDownVisible}
          aria-haspopup="listbox"
          aria-label={HEADER_SEARCH.searchInputAriaLabel}
          autoComplete="off"
          className="input"
          data-qa="enterSearch"
          data-testid="search-input"
          defaultValue={searchQueryDefault}
          id="header-search"
          onBlur={() => blurHandler()}
          onChange={(event) => changeHandler(event)}
          onFocus={() => focusHandler()}
          onKeyDown={(event) => inputKeyDownHandler(event)}
          placeholder={
            defaultPlaceholder && defaultPlaceholder.length
              ? defaultPlaceholder
              : HEADER_SEARCH.searchPlaceholder
          }
          ref={textInput}
          role="combobox"
          type="text"
        />
        {SHOW_SEARCH_LOCATION && (
          <input
            aria-activedescendant={activeDescendent}
            aria-controls="search-suggestions"
            aria-haspopup="listbox"
            aria-label={HEADER_SEARCH.searchInputAriaLabel}
            autoComplete="off"
            className="input-location"
            data-qa="enterSearchLocation"
            data-testid="search-input-location"
            defaultValue={locationName}
            id="header-search-location"
            onClick={() => inputLocationHandler()}
            placeholder={'Select your location'}
            readOnly
            ref={textInput}
            type="text"
          />
        )}
        <button
          aria-label={HEADER_SEARCH.searchButtonAriaLabel}
          className="button search-input__submit-button"
          data-testid="search-submit-button"
          disabled={isDisabled}
          onClick={(event) => searchButtonClickHandler(event, searchQuery)}
          type="button"
        >
          <Icon color="none" faIcon={faSearch} size="19px" />
        </button>
      </form>

      {isDropDownVisible && (
        <HeaderSearchDropDown
          dropDownTitle={dropDownTitle}
          liClickHandler={liClickHandler}
          query={searchQuery}
          suggestionFocusIndex={suggestionFocusIndex}
          suggestions={suggestions}
        />
      )}
      <HeaderLocationDropDown
        setShowModal={setShowModal}
        showModal={showModal}
      />
      <style jsx>{`
        .search-input__submit-button:disabled {
          cursor: not-allowed;
        }
        .search-container {
          display: block;
          margin-top: 2px;
          position: relative;
        }
        .button {
          background-color: ${theme.colors.searchbuttonbg};
          border: 0;
          border-radius: 0 20px 20px 0;
          color: ${theme.colors.searchborder};
          height: 31px;
          position: absolute;
          right: 1px;
          top: 1px;
          width: 38px;
        }
        .input {
          background-color: ${theme.colors.searchbg};
          border-color: ${theme.colors.searchborder};
          border-width: 1px;
          border-style: solid;
          border-top-left-radius: 20px;
          border-bottom-left-radius: 20px;
          border-top-right-radius: ${SHOW_SEARCH_LOCATION ? '0' : '20px'};
          border-bottom-right-radius: ${SHOW_SEARCH_LOCATION ? '0' : '20px'};
          color: ${theme.colors.searchtext};
          height: 33px;
          margin-bottom: 6px;
          padding: 0 45px 0 8px;
          width: ${SHOW_SEARCH_LOCATION ? '65%' : '100%'};
        }
        .input-location {
          background-color: ${theme.colors.searchbg};
          border-bottom-left-radius: 0px;
          border-bottom-right-radius: 20px;
          border-color: ${theme.colors.searchborder};
          border-style: solid;
          border-top-left-radius: 0px;
          border-top-right-radius: 20px;
          border-width: 1px;
          color: ${theme.colors.searchtext};
          height: 33px;
          margin-bottom: 6px;
          overflow: hidden;
          padding: 0 45px 0 8px;
          text-overflow: ellipsis;
          white-space: nowrap;
          width: 35%;
        }
        .input::placeholder {
          font-size: 12px;
          color: ${theme.colors.searchplaceholder};
          text-transform: ${defaultPlaceholder && defaultPlaceholder.length
            ? theme.text.error404placeholder
            : theme.text.search};
        }
        .input:focus {
          border-color: ${theme.colors.searchborderfocused};
          background-color: ${theme.colors.searchbgfocused};
          outline: none;
        }
        .button:focus {
          background-color: ${theme.colors.searchbuttonbgfocused};
          color: ${theme.colors.searchbuttonfocused};
        }
        .input:focus ~ .button {
          background-color: ${theme.colors.searchbuttonbgfocused};
          color: ${theme.colors.searchbuttonfocused};
        }
        .button:focus ~ .input {
          border-color: ${theme.colors.searchborderfocused};
          background-color: ${theme.colors.searchbgfocused};
          outline: none;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .button {
            height: 38px;
            width: 38px;
          }
          .input {
            height: 40px;
            margin-bottom: 0;
            width: ${SHOW_SEARCH_LOCATION ? '75%' : '100%'};
          }
          .input-location {
            height: 40px;
            margin-bottom: 0;
            width: 25%;
          }
          .input::placeholder {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};

HeaderSearch.propTypes = {
  defaultPlaceholder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

HeaderSearch.defaultProps = {
  defaultPlaceholder: '',
};

export default HeaderSearch;
